import React, { useEffect, useMemo, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { CheckCircle } from 'lucide-react';

const PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

// Load Stripe.js once at module scope.
const stripePromise = PUBLISHABLE_KEY ? loadStripe(PUBLISHABLE_KEY) : null;

// Turn a raw error/response body into a clean, human-readable message.
// Backend errors arrive as JSON like {"detail":"Invoice already paid"} — never
// show that raw to a paying customer.
const cleanErrorMessage = (raw) => {
  if (!raw) return 'Failed to start payment';
  const s = String(raw).trim();
  if (s.startsWith('{')) {
    try {
      const parsed = JSON.parse(s);
      if (parsed && parsed.detail) {
        return typeof parsed.detail === 'string' ? parsed.detail : JSON.stringify(parsed.detail);
      }
    } catch (_) { /* not JSON */ }
  }
  return s;
};

/**
 * V2 dark-themed Stripe appearance.
 * Mirrors portal-v2.css tokens: ink black bg, JetBrains Mono labels,
 * red focus + Pay button. Squared corners (borderRadius 0).
 */
const v2Appearance = {
  theme: 'night',
  variables: {
    colorPrimary: '#dc2626',
    colorBackground: '#16181a',
    colorText: '#ffffff',
    colorTextSecondary: 'rgba(255,255,255,0.65)',
    colorTextPlaceholder: 'rgba(255,255,255,0.40)',
    colorDanger: '#f87171',
    colorIcon: 'rgba(255,255,255,0.65)',
    colorIconTab: '#ffffff',
    colorIconTabSelected: '#dc2626',
    fontFamily: 'JetBrains Mono, ui-monospace, monospace',
    fontSizeBase: '14px',
    fontWeightNormal: '400',
    fontWeightMedium: '500',
    spacingUnit: '4px',
    borderRadius: '0px',
  },
  rules: {
    '.Input': {
      backgroundColor: '#0a0a0a',
      border: '1px solid #292929',
      padding: '14px 14px',
      color: '#ffffff',
      transition: 'border-color 150ms ease',
    },
    '.Input:focus': { border: '1px solid #dc2626', boxShadow: 'none', outline: 'none' },
    '.Input--invalid': { border: '1px solid #f87171', boxShadow: 'none' },
    '.Label': {
      color: 'rgba(255,255,255,0.65)',
      fontSize: '11px',
      letterSpacing: '0.18em',
      textTransform: 'uppercase',
      fontWeight: '400',
      marginBottom: '6px',
    },
    '.Tab': {
      backgroundColor: '#0a0a0a',
      border: '1px solid #292929',
      color: 'rgba(255,255,255,0.65)',
    },
    '.Tab:hover': { color: '#ffffff', border: '1px solid #3a3a3a' },
    '.Tab--selected': {
      backgroundColor: '#16181a',
      border: '1px solid #dc2626',
      color: '#ffffff',
    },
    '.TabIcon--selected': { fill: '#dc2626' },
    '.Block': { backgroundColor: '#0a0a0a', border: '1px solid #292929' },
    '.Error': { color: '#f87171', fontSize: '12px' },
  },
};

const fmtUSD = (v) =>
  `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Inner form (must live inside <Elements>) — collects payment details and submits.
 */
const PaymentForm = ({ amount, onSuccess, returnUrl }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setErrorMessage(null);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      redirect: 'if_required',
    });

    if (error) {
      setErrorMessage(error.message || 'Payment could not be completed');
      setSubmitting(false);
      return;
    }

    if (paymentIntent && paymentIntent.status === 'succeeded') {
      onSuccess && onSuccess(paymentIntent);
      return;
    }
    // Otherwise Stripe redirected (e.g. 3DS). Browser navigation will take over.
  };

  return (
    <form onSubmit={handleSubmit} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }} data-testid="pv2-payment-form">
      <PaymentElement options={{ layout: 'tabs' }} />
      {errorMessage && (
        <div className="pv2-alert pv2-alert-red" style={{ marginTop: 4 }} data-testid="pv2-payment-error">
          <span>{errorMessage}</span>
        </div>
      )}
      <button
        type="submit"
        disabled={!stripe || submitting}
        className="pv2-btn pv2-btn-primary"
        style={{ width: '100%', padding: '16px', fontSize: 13, opacity: submitting ? 0.7 : 1 }}
        data-testid="pv2-payment-submit"
      >
        {submitting ? 'Processing…' : `Pay ${fmtUSD(amount)}`}
      </button>
      <div style={{ textAlign: 'center', fontSize: 11, color: 'rgba(255,255,255,0.40)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
        Secured by Stripe · 256-bit TLS
      </div>
    </form>
  );
};

/**
 * V2-themed Stripe Payment Element wrapper.
 *
 * Props:
 *   - createIntent: async () => ({ client_secret, amount })
 *       Calls your backend to create a PaymentIntent.
 *   - returnUrl: absolute URL Stripe will redirect to if 3DS is required.
 *   - onSuccess: (paymentIntent) => void (called when no redirect was needed).
 */
const EmbeddedStripePaymentElement = ({ createIntent, returnUrl, onSuccess, onAlreadyPaid }) => {
  const [clientSecret, setClientSecret] = useState(null);
  const [amount, setAmount] = useState(null);
  const [error, setError] = useState(null);
  const [alreadyPaid, setAlreadyPaid] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await createIntent();
        if (cancelled) return;
        if (!data || !data.client_secret) throw new Error('No client_secret returned');
        setClientSecret(data.client_secret);
        setAmount(data.amount);
      } catch (e) {
        if (cancelled) return;
        const clean = cleanErrorMessage(e.message);
        if (/already\s+paid/i.test(clean)) {
          if (onAlreadyPaid) { onAlreadyPaid(); return; }
          setAlreadyPaid(true);
        } else {
          setError(clean);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [createIntent, onAlreadyPaid]);

  const options = useMemo(() => ({
    clientSecret,
    appearance: v2Appearance,
    fonts: [{ cssSrc: 'https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&display=swap' }],
  }), [clientSecret]);

  if (!PUBLISHABLE_KEY) {
    return (
      <div style={{ padding: 24, color: '#fca5a5', fontFamily: 'JetBrains Mono, monospace', fontSize: 13 }}>
        Stripe publishable key not configured. Set REACT_APP_STRIPE_PUBLISHABLE_KEY in frontend/.env.
      </div>
    );
  }

  if (alreadyPaid) {
    return (
      <div className="pv2-alert pv2-alert-green" style={{ margin: 24 }} data-testid="pv2-payment-already-paid">
        <CheckCircle size={14} />
        <span>This invoice has already been paid in full. Thank you!</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pv2-alert pv2-alert-red" style={{ margin: 24 }} data-testid="pv2-payment-init-error">
        <span>{error}</span>
      </div>
    );
  }

  if (!clientSecret) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'rgba(255,255,255,0.65)', fontFamily: 'JetBrains Mono, monospace', fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase' }} data-testid="pv2-payment-loading">
        Initializing secure form…
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise} options={options}>
      <PaymentForm amount={amount} onSuccess={onSuccess} returnUrl={returnUrl} />
    </Elements>
  );
};

export default EmbeddedStripePaymentElement;
